import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getBudgets, createBudget, deleteBudget, getBudgetComparison, getAccounts } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Plus, Trash, CurrencyDollar, TrendUp, TrendDown } from '@phosphor-icons/react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function CustomTooltip({ active, payload, label }) {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-3 shadow-sm">
        <p className="text-xs font-mono text-zinc-500 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={p.dataKey || p.name || `payload-${i}`} className="text-sm" style={{ color: p.color }}>
            {p.name}: <span className="font-mono font-medium">${p.value?.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function BudgetPage() {
  const [budgets, setBudgets] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [year] = useState(2026);
  const [selectedComparison, setSelectedComparison] = useState(null);

  const [form, setForm] = useState({
    account_id: '',
    account_name: '',
    mode: 'annual', // annual or monthly
    annual_amount: '',
    monthly_amounts: {}
  });

  useEffect(() => {
    fetchData();
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [budgetRes, compRes, acctRes] = await Promise.all([
        getBudgets(year),
        getBudgetComparison(year),
        getAccounts()
      ]);
      setBudgets(budgetRes.data.budgets);
      setComparison(compRes.data.comparison);
      setAccounts(acctRes.data.accounts);
    } catch (error) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        account_id: form.account_id,
        account_name: form.account_name || accounts.find(a => a.account_id === form.account_id)?.name || form.account_id,
        year
      };
      if (form.mode === 'annual') {
        data.annual_amount = parseFloat(form.annual_amount);
      } else {
        data.monthly_amounts = form.monthly_amounts;
      }
      await createBudget(data);
      toast.success('Budget saved');
      setIsDialogOpen(false);
      setForm({ account_id: '', account_name: '', mode: 'annual', annual_amount: '', monthly_amounts: {} });
      fetchData();
    } catch (error) {
      toast.error('Failed to save budget');
    }
  };

  const handleDelete = async (budgetId) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      await deleteBudget(budgetId);
      toast.success('Budget deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  if (loading) {
    return (
      <Layout title="Budget vs Actual">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Build comparison chart data
  const chartData = MONTHS.map((month, i) => {
    const monthKey = `${(i + 1).toString().padStart(2, '0')}`;
    const row = { month };
    let totalBudget = 0;
    let totalActual = 0;
    comparison.forEach(c => {
      const m = c.monthly?.find(m => m.month === monthKey);
      if (m) {
        totalBudget += m.budget;
        totalActual += m.actual;
      }
    });
    row.budget = Math.round(totalBudget);
    row.actual = Math.round(totalActual);
    return row;
  });

  return (
    <Layout title="Budget vs Actual">
      <div data-testid="budget-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-600">
              Set annual or monthly budgets per account and compare against actual posted transactions.
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="add-budget-button">
                <Plus size={18} className="mr-2" />
                Add Budget
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="budget-dialog">
              <DialogHeader>
                <DialogTitle>Set Budget</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Account</Label>
                  <Select value={form.account_id} onValueChange={(v) => {
                    const acct = accounts.find(a => a.account_id === v);
                    setForm({ ...form, account_id: v, account_name: acct ? `${acct.code} - ${acct.name}` : v });
                  }}>
                    <SelectTrigger className="rounded-sm" data-testid="budget-account-select">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => (
                        <SelectItem key={a.account_id} value={a.account_id}>{a.code} - {a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Tabs value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <TabsList className="bg-zinc-100 p-1 rounded-sm grid grid-cols-2">
                    <TabsTrigger value="annual" className="rounded-sm text-xs" data-testid="budget-mode-annual">Annual (Even Split)</TabsTrigger>
                    <TabsTrigger value="monthly" className="rounded-sm text-xs" data-testid="budget-mode-monthly">Custom Monthly</TabsTrigger>
                  </TabsList>

                  <TabsContent value="annual" className="mt-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Annual Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.annual_amount}
                        onChange={(e) => setForm({ ...form, annual_amount: e.target.value })}
                        placeholder="e.g., 12000"
                        className="rounded-sm font-mono"
                        required={form.mode === 'annual'}
                        data-testid="budget-annual-amount"
                      />
                      <p className="text-xs text-zinc-400">Will be split evenly: ${form.annual_amount ? (parseFloat(form.annual_amount) / 12).toFixed(2) : '0.00'}/month</p>
                    </div>
                  </TabsContent>

                  <TabsContent value="monthly" className="mt-4">
                    <div className="grid grid-cols-3 gap-3">
                      {MONTHS.map((m, i) => {
                        const key = `${(i + 1).toString().padStart(2, '0')}`;
                        return (
                          <div key={key} className="space-y-1">
                            <Label className="text-xs text-zinc-500">{m}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={form.monthly_amounts[key] || ''}
                              onChange={(e) => setForm({
                                ...form,
                                monthly_amounts: { ...form.monthly_amounts, [key]: parseFloat(e.target.value) || 0 }
                              })}
                              placeholder="0"
                              className="rounded-sm font-mono h-9 text-sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="save-budget-button">Save Budget</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Overall Comparison Chart */}
        {comparison.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="budget-chart">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Monthly Budget vs Actual ({year})</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="budget" name="Budget" fill="#E4E4E7" radius={[2, 2, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#09090B" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Per-Account Table */}
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h3 className="font-semibold">Budget by Account</h3>
          </div>
          {budgets.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <CurrencyDollar size={48} className="mx-auto mb-4 text-zinc-300" />
              <p className="font-medium mb-1">No budgets set</p>
              <p className="text-sm">Add budgets to start tracking actual vs planned spending.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr className="text-left">
                    <th className="px-6 pt-4">Account</th>
                    <th className="px-6 pt-4 text-right">Annual Budget</th>
                    <th className="px-6 pt-4 text-right">Annual Actual</th>
                    <th className="px-6 pt-4 text-right">Variance</th>
                    <th className="px-6 pt-4">Status</th>
                    <th className="px-6 pt-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c) => {
                    const budget = budgets.find(b => b.account_id === c.account_id);
                    const overBudget = c.annual_variance > 0;
                    return (
                      <tr key={c.account_id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => setSelectedComparison(selectedComparison?.account_id === c.account_id ? null : c)}>
                        <td className="px-6 text-sm font-medium">{c.account_name}</td>
                        <td className="px-6 text-right font-mono text-sm">${c.annual_budget.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="px-6 text-right font-mono text-sm">${c.annual_actual.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className={`px-6 text-right font-mono text-sm ${overBudget ? 'text-red-600' : 'text-emerald-600'}`}>
                          {overBudget ? '+' : ''}${c.annual_variance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6">
                          {overBudget ? (
                            <span className="flex items-center gap-1 text-xs text-red-600"><TrendUp size={14} /> Over</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-emerald-600"><TrendDown size={14} /> Under</span>
                          )}
                        </td>
                        <td className="px-6">
                          {budget && (
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(budget.budget_id); }} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm" data-testid={`delete-budget-${budget.budget_id}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Selected account monthly detail */}
        {selectedComparison && (
          <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="budget-monthly-detail">
            <h3 className="font-semibold mb-4">{selectedComparison.account_name} — Monthly Detail</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={selectedComparison.monthly.map((m, i) => ({ month: MONTHS[i], budget: m.budget, actual: m.actual }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(v) => `$${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="budget" name="Budget" fill="#E4E4E7" radius={[2, 2, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#09090B" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Layout>
  );
}
